import { Dispatch, SetStateAction, useEffect, useState } from 'react';

/**
 * A hook that manages the state of a controlled component.
 * @param defaultValue The default value of the state.
 * @param controlledValue The current value of the controlled component.
 * @param onControlledChange A function that is called when the controlled component's value changes.
 * @returns A tuple containing the current state and a function to update the state.
 */
export function useControlledState<S>(
	defaultValue: S,
	controlledValue?: S,
	onControlledChange?: (value: S) => void,
): [S, Dispatch<SetStateAction<S>>] {
	const [state, setState] = useState(defaultValue);

	useEffect(() => {
		if (controlledValue !== undefined) {
			setState(controlledValue);
		}
	}, [controlledValue]);

	useEffect(() => {
		if (onControlledChange !== undefined) {
			onControlledChange(state);
		}
	}, [state]);

	return [controlledValue !== undefined ? controlledValue : state, setState];
}
